import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Grid,
  LinearProgress
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import { useTranslation } from 'react-i18next';

export const getPasswordCriteria = (
  password = '',
  confirmPassword = null,
  t = (s) => s
) => {
  const list = [
    {
      key: 'length',
      label: t('At least 12 characters'),
      met: password.length >= 12
    },
    {
      key: 'upper',
      label: t('At least one uppercase letter (A-Z)'),
      met: /[A-Z]/.test(password)
    },
    {
      key: 'lower',
      label: t('At least one lowercase letter (a-z)'),
      met: /[a-z]/.test(password)
    },
    {
      key: 'number',
      label: t('At least one number (0-9)'),
      met: /[0-9]/.test(password)
    },
    {
      key: 'special',
      label: t('At least one special character (!@#$%...)'),
      met: /[^A-Za-z0-9]/.test(password)
    }
  ];

  if (confirmPassword !== null && confirmPassword !== undefined) {
    list.push({
      key: 'match',
      label: t('Confirm password matches'),
      met: Boolean(
        password && confirmPassword && password === confirmPassword
      )
    });
  }

  return list;
};

export const isPasswordPolicyMet = (
  password = '',
  confirmPassword = null
) => {
  const isPolicyMet =
    Boolean(password) &&
    password.length >= 12 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[^A-Za-z0-9]/.test(password);

  if (confirmPassword !== null && confirmPassword !== undefined) {
    return (
      isPolicyMet &&
      Boolean(confirmPassword) &&
      password === confirmPassword
    );
  }

  return isPolicyMet;
};

const PasswordPolicyChecklist = ({
  password = '',
  confirmPassword = null,
  sx = {}
}) => {
  const { t } = useTranslation();
  const criteria = getPasswordCriteria(password, confirmPassword, t);
  const metCount = criteria.filter((c) => c.met).length;

  let strengthLabel = t('Very Weak');
  let strengthColor = '#FF1943';

  if (metCount >= criteria.length) {
    strengthLabel = t('Strong');
    strengthColor = '#57CA22';
  } else if (metCount >= 3) {
    strengthLabel = t('Moderate');
    strengthColor = '#FFA319';
  } else if (metCount >= 1) {
    strengthLabel = t('Weak');
    strengthColor = '#FF1943';
  }

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 2,
        p: 2,
        borderRadius: 1.5,
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.04)'
            : 'rgba(0,0,0,0.02)',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        ...sx
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t('Password Policy Requirements')}:
        </Typography>
        {password.length > 0 && (
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: strengthColor }}
          >
            {strengthLabel}
          </Typography>
        )}
      </Box>

      {password.length > 0 && (
        <LinearProgress
          variant="determinate"
          value={(metCount / criteria.length) * 100}
          sx={{
            height: 6,
            borderRadius: 3,
            mb: 1.5,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.1)'
                : 'rgba(0,0,0,0.08)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: strengthColor,
              borderRadius: 3
            }
          }}
        />
      )}

      <Grid container spacing={0.75}>
        {criteria.map((item) => (
          <Grid item xs={12} sm={6} key={item.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {item.met ? (
                <CheckCircleRoundedIcon
                  sx={{ fontSize: 16, color: '#57CA22' }}
                />
              ) : (
                <RadioButtonUncheckedRoundedIcon
                  sx={{
                    fontSize: 16,
                    color: (theme) => theme.palette.text.disabled
                  }}
                />
              )}
              <Typography
                variant="caption"
                sx={{
                  color: item.met
                    ? (theme) =>
                        theme.palette.mode === 'dark' ? '#79d854' : '#2e7d32'
                    : 'text.secondary',
                  fontWeight: item.met ? 600 : 400
                }}
              >
                {item.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

PasswordPolicyChecklist.propTypes = {
  password: PropTypes.string,
  confirmPassword: PropTypes.string,
  sx: PropTypes.object
};

export default PasswordPolicyChecklist;
